import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { ManualSection } from '../types/help-center.types';

interface ChapterSidebarProps {
  sections: ManualSection[];
  activeId: string;
  onSelect: (id: string) => void;
}

export function ChapterSidebar({ sections, activeId, onSelect }: ChapterSidebarProps) {
  // Find which section contains the active chapter and expand it by default
  const activeSectionId = sections.find((s) => s.chapters.some((c) => c.id === activeId))?.id;
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(activeSectionId ? [activeSectionId] : [sections[0]?.id])
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleSelect = (chapterId: string, sectionId: string) => {
    // Auto-expand the section when selecting a chapter
    setExpandedSections((prev) => new Set(prev).add(sectionId));
    onSelect(chapterId);
  };

  return (
    <nav className="flex flex-col gap-0.5 max-h-[70vh] overflow-y-auto pr-1">
      {sections.map((section) => {
        const isExpanded = expandedSections.has(section.id);
        const hasActiveChapter = section.chapters.some((c) => c.id === activeId);

        return (
          <div key={section.id}>
            {/* Section header */}
            <button
              onClick={() => toggleSection(section.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide transition-colors',
                'hover:bg-muted/50',
                hasActiveChapter ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                  !isExpanded && '-rotate-90'
                )}
              />
              <span className="truncate">{section.title}</span>
              <span className="ml-auto text-[10px] font-normal text-muted-foreground">
                {section.chapters.length}
              </span>
            </button>

            {/* Chapters in section */}
            <div
              className={cn(
                'overflow-hidden transition-all duration-200',
                isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              )}
            >
              <div className="ml-2 border-l border-muted pl-1 space-y-0.5 pb-1">
                {section.chapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    onClick={() => handleSelect(chapter.id, section.id)}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                      'hover:bg-muted/80',
                      activeId === chapter.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                        activeId === chapter.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {chapter.number}
                    </span>
                    <span className="leading-snug text-[13px]">{chapter.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}
